import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, AppBar, Toolbar, Typography, List, ListItem,
  ListItemIcon, ListItemText, ListItemButton, Avatar, Divider,
  IconButton, Tooltip, Menu, MenuItem
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Description as QuoteIcon,
  Receipt as InvoiceIcon,
  Payment as PaymentIcon,
  Inventory as ProductIcon,
  CampaignOutlined as CampaignIcon,
  Menu as MenuIcon,
  Logout,
  Settings as SettingsIcon,
  People as UsersIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const DRAWER_WIDTH = 260;

const NAV = [
  { label: 'Panel', icon: <DashboardIcon />, path: '/dashboard' },
  { label: 'Clientes', icon: <PeopleIcon />, path: '/clientes' },
  { label: 'Cotizaciones', icon: <QuoteIcon />, path: '/cotizaciones' },
  { label: 'Facturas', icon: <InvoiceIcon />, path: '/facturas' },
  { label: 'Pagos', icon: <PaymentIcon />, path: '/pagos' },
  { label: 'Productos', icon: <ProductIcon />, path: '/productos' },
  { label: 'Marketing', icon: <CampaignIcon />, path: '/marketing' },
];

const NAV_BOTTOM = [
  { label: 'Usuarios', icon: <UsersIcon />, path: '/configuracion/usuarios' },
];

const IDNLogo = () => (
  <Box sx={{ px: 2, pt: 2.5, pb: 1.5 }}>
    {/* Icon: show only the purple circle part of the logo */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{
        width: 48, height: 48, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
        background: '#7C3AED'
      }}>
        <img
          src="/idn-logo.png"
          alt="IDN"
          style={{
            height: '100%',
            width: 'auto',
            display: 'block',
          }}
        />
      </Box>
      <Box>
        <Box sx={{ color: 'white', fontWeight: 800, fontSize: '1.1rem', lineHeight: 1.1, letterSpacing: '-0.02em' }}>
          IDN
        </Box>
        <Box sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          Innovación
        </Box>
      </Box>
    </Box>
  </Box>
);

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <IDNLogo />
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: 2, mb: 1 }} />
      <List sx={{ flex: 1, px: 1.5, pt: 1 }}>
        {NAV.map(item => {
          const active = location.pathname.startsWith(item.path);
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => { navigate(item.path); setMobileOpen(false); }}
                sx={{
                  borderRadius: '10px', py: 1.2, px: 1.5,
                  background: active ? 'rgba(147,51,234,0.25)' : 'transparent',
                  '&:hover': { background: active ? 'rgba(147,51,234,0.3)' : 'rgba(255,255,255,0.06)' },
                  transition: 'all 0.15s ease'
                }}
              >
                <ListItemIcon sx={{
                  color: active ? '#C084FC' : 'rgba(255,255,255,0.45)',
                  minWidth: 36,
                  '& svg': { fontSize: '1.2rem' }
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.label} sx={{
                  '& .MuiListItemText-primary': {
                    fontSize: '0.875rem',
                    fontWeight: active ? 700 : 500,
                    color: active ? 'white' : 'rgba(255,255,255,0.6)'
                  }
                }} />
                {active && (
                  <Box sx={{
                    width: 4, height: 20, borderRadius: 2,
                    background: 'linear-gradient(#C084FC, #9333EA)'
                  }} />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: 2, mb: 0.5 }} />
      <List sx={{ px: 1.5, pb: 0.5 }}>
        {NAV_BOTTOM.map(item => {
          const active = location.pathname.startsWith(item.path);
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => { navigate(item.path); setMobileOpen(false); }}
                sx={{
                  borderRadius: '10px', py: 1, px: 1.5,
                  background: active ? 'rgba(147,51,234,0.25)' : 'transparent',
                  '&:hover': { background: active ? 'rgba(147,51,234,0.3)' : 'rgba(255,255,255,0.06)' },
                }}
              >
                <ListItemIcon sx={{ color: active ? '#C084FC' : 'rgba(255,255,255,0.45)', minWidth: 36, '& svg': { fontSize: '1.2rem' } }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.label} sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem', fontWeight: active ? 700 : 500, color: active ? 'white' : 'rgba(255,255,255,0.6)' } }} />
                {active && <Box sx={{ width: 4, height: 20, borderRadius: 2, background: 'linear-gradient(#C084FC, #9333EA)' }} />}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: 2, mb: 1 }} />
      <Box sx={{ p: 2 }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          p: 1.5, borderRadius: '10px',
          background: 'rgba(255,255,255,0.05)',
          cursor: 'pointer', '&:hover': { background: 'rgba(255,255,255,0.08)' }
        }}
          onClick={e => setAnchorEl(e.currentTarget)}
        >
          <Avatar sx={{ width: 32, height: 32, background: 'linear-gradient(135deg, #9333EA, #06B6D4)', fontSize: '0.8rem', fontWeight: 700 }}>
            {user?.name?.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ color: 'white', fontSize: '0.8rem', fontWeight: 600, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.name}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.68rem' }}>
              {user?.role}
            </Typography>
          </Box>
        </Box>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          <MenuItem onClick={() => { setAnchorEl(null); logout(); navigate('/login'); }}>
            <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
            Cerrar sesión
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar - desktop */}
      <Drawer variant="permanent" sx={{
        width: DRAWER_WIDTH, flexShrink: 0,
        display: { xs: 'none', md: 'block' },
        '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' }
      }}>
        {drawerContent}
      </Drawer>

      {/* Sidebar - mobile */}
      <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}
      >
        {drawerContent}
      </Drawer>

      {/* Main content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Mobile AppBar */}
        <AppBar position="sticky" sx={{
          display: { md: 'none' },
          background: 'linear-gradient(135deg, #4C1D95, #1E1B2E)',
          boxShadow: 'none'
        }}>
          <Toolbar>
            <IconButton color="inherit" onClick={() => setMobileOpen(true)} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>IDN CRM</Typography>
          </Toolbar>
        </AppBar>

        <Box sx={{ flex: 1, p: { xs: 2, md: 3 }, overflow: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
